/**
 * Fullscreen, where the browser allows it.
 *
 * `document.fullscreenEnabled` is false where fullscreen cannot happen: an
 * iPhone (no element fullscreen at all) and a frame without
 * `allow="fullscreen"`. The `webkit` names cover older Android WebViews and
 * iPad Safari.
 */
import type { HostFullscreen } from '@puzzlemania/core';

type WebkitDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};
type WebkitElement = HTMLElement & { webkitRequestFullscreen?: () => void };

/** The browser allows fullscreen in this frame at all. */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as WebkitDocument;
  return d.fullscreenEnabled === true || d.webkitFullscreenEnabled === true;
}

/** The page is fullscreen right now. */
export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as WebkitDocument;
  return (d.fullscreenElement ?? d.webkitFullscreenElement ?? null) !== null;
}

/** True when a request could succeed and the page is not already fullscreen. */
export function canGoFullscreen(): boolean {
  return fullscreenSupported() && !isFullscreen();
}

/** Leave fullscreen. Harmless when not fullscreen. */
export async function exitFullscreen(): Promise<void> {
  const d = document as WebkitDocument;
  if (!isFullscreen()) return;
  try {
    if (typeof d.exitFullscreen === 'function') await d.exitFullscreen();
    else d.webkitExitFullscreen?.();
  } catch {
    // Already left by the system gesture; nothing to do.
  }
}

/** Calls `listener` whenever fullscreen starts or ends, by any means. */
export function onFullscreenChange(listener: () => void): () => void {
  document.addEventListener('fullscreenchange', listener);
  document.addEventListener('webkitfullscreenchange', listener);
  return () => {
    document.removeEventListener('fullscreenchange', listener);
    document.removeEventListener('webkitfullscreenchange', listener);
  };
}

/**
 * The game's full-screen capability (`GameHostCapabilities.fullscreen`), or
 * `undefined` where the host does not permit the control or the browser
 * cannot go fullscreen in this frame.
 */
export function createHostFullscreen(permitted: boolean): HostFullscreen | undefined {
  if (!permitted || !fullscreenSupported()) return undefined;
  return {
    isActive: isFullscreen,
    toggle: async () => {
      if (isFullscreen()) await exitFullscreen();
      else await goFullscreen();
    },
    subscribe: onFullscreenChange,
  };
}

/** Must run inside a user gesture (a click), or the browser refuses it. */
export async function goFullscreen(): Promise<boolean> {
  const el = document.documentElement as WebkitElement;
  try {
    if (typeof el.requestFullscreen === 'function') {
      await el.requestFullscreen({ navigationUI: 'hide' });
    } else if (typeof el.webkitRequestFullscreen === 'function') {
      el.webkitRequestFullscreen();
    } else {
      return false;
    }
    return true;
  } catch {
    // Refused (no gesture, policy, user setting). The screen asking for room
    // stays up, which is the honest outcome.
    return false;
  }
}

/** Whether a profile's `fullscreenButton.hosts` names this host. */
export function hostAllows(
  keys: readonly string[] | undefined,
  isHost: ((keys: readonly string[]) => boolean) | undefined,
): boolean {
  if (!keys) return false;
  return isHost ? isHost(keys) : keys.includes('*');
}
